import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@mui/material';
import { format } from 'date-fns';

import { ProfileFlags } from '@labradorsports/constants';
import { noPropagation, useSite } from '@labradorsports/utils';

import { ProfilePicture, useDispatcher } from '../../shared/index.js';
import { useNavigateWithQuery } from '../../shared/providers/index.js';
import { communityActions, Selectors, RootState } from '../../store/index.js';
import AddCommentForm from '../add-comment-form/add-comment-form.js';

interface Props {
    comments: any[];
    addComment: (text: string) => void;
}

const CommunityPlayComments: FC<Props> = ({ comments, addComment }) => {
    const { Config } = useSite();
    const navigate = useNavigateWithQuery();
    const showSignupPrompt = useDispatcher(communityActions.ShowSignupPrompt);
    const user = useSelector(Selectors.user);
    const hasProfile = useSelector((state: RootState) =>
        Selectors.userHasFlag(state, ProfileFlags.HAS_COMMUNITY_PROFILE)
    );
    const [showCommentForm, setShowCommentForm] = useState(false);

    if (!comments) {
        return null;
    }

    const onAddCommentClick = () => {
        if (!user || !hasProfile) {
            showSignupPrompt(true);
        } else {
            setShowCommentForm(true);
        }
    };

    const onCancelComment = () => {
        setShowCommentForm(false);
    };

    const onSubmitComment = (text: string) => {
        addComment(text);
        onCancelComment();
    };

    const profileClick = (sharedBy) =>
        noPropagation((evt) => {
            evt.preventDefault();
            navigate('/community/profile', {
                communityProfileId: sharedBy,
            });
        });

    return (
        <div className="community-play-comments card no-padding">
            {comments.length > 0 ? (
                comments.map((comment, idx) => (
                    <div className={`community-play-comment ${comment.awaitingModeration ? 'pending' : ''}`} key={idx}>
                        <p>{comment.text}</p>
                        <div className="sharing-details small">
                            <a
                                href={`/community/profile?pr=${comment.sharedBy}`}
                                className="button-link profile-link"
                                onClick={profileClick(comment.sharedBy)}
                            >
                                <ProfilePicture uid={comment?.ownerId} />
                                {comment.sharedBy}
                            </a>
                            <span className="divider">|</span>
                            <span className="text-muted">{format(comment.createdDate, 'MMM d, yyyy h:mm a')}</span>
                        </div>
                        {comment.awaitingModeration ? <span className="comment-pending">Pending</span> : null}
                    </div>
                ))
            ) : (
                <div className="p-3">No comments yet.</div>
            )}
            {showCommentForm ? (
                <AddCommentForm onSubmit={onSubmitComment} onCancel={onCancelComment} />
            ) : (
                <div className="p-3">
                    <hr />
                    {user ? (
                        <div className="text-end">
                            <Button onClick={onAddCommentClick}>Add Comment</Button>
                        </div>
                    ) : (
                        <>
                            <p>
                                <b>Join our fast-growing community of {Config.SiteConfig.Sport} coaches!</b>
                            </p>
                            <Button href="/login" className="me-2">
                                Log In
                            </Button>
                            <Button href="/signup" color="secondary" variant="contained">
                                Sign Up
                            </Button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default CommunityPlayComments;
